import React, { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  UserIcon, 
  UsersIcon,
  ArrowRightEndOnRectangleIcon,
  PaperAirplaneIcon,
  MapIcon,
} from '@heroicons/react/24/outline';
import { getImageUrl } from '../config/runtime';
import {
  UserIcon as UserIconSolid, 
  UsersIcon as UsersIconSolid,
  PaperAirplaneIcon as PaperAirplaneIconSolid,
  MapIcon as MapIconSolid,
} from '@heroicons/react/24/solid';
import { useAuth } from '../contexts/AuthContext';
import { useBottomNav } from '../contexts/BottomNavContext';
import { useQuery } from '@tanstack/react-query';
import { projectAPI, createQueryKey } from '../services/api';
import { useProjectStore } from '../stores/projectStore';
import { useMobile, useKeyboard } from '../hooks/useMobile';
import { HeaderSlotContext, HEADER_SEARCH_SLOT_ID } from '../contexts/HeaderSlotContext';

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const { currentProject } = useProjectStore();
  const { isBottomNavVisible } = useBottomNav();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isDesktopSearchVisible, setDesktopSearchVisible] = useState(false);

  const headerSlotValue = useMemo(
    () => ({ setDesktopSearchVisible }),
    [setDesktopSearchVisible]
  );
  
  // Mobile hooks
  const { isMobile, viewportHeight } = useMobile();
  const { isKeyboardOpen, keyboardHeight } = useKeyboard();

  // Active project indicator (fallback to API if store not set)
  const { data: activeProjectResponse } = useQuery({
    queryKey: createQueryKey.activeProject(),
    queryFn: () => projectAPI.getActive(),
    enabled: !!isAuthenticated,
    staleTime: 5000,
    retry: false,
  });
  const activeProject = activeProjectResponse?.data?.project || activeProjectResponse?.data;
  // Note: 'paused' status is deprecated - Events should finish, Sites use session archiving
  // Keeping this check for backward compatibility with legacy projects
  const hasActiveProject = Boolean(
    (currentProject && (currentProject.status === 'ongoing' || currentProject.status === 'paused')) ||
    (activeProject && (activeProject.status === 'ongoing' || activeProject.status === 'paused'))
  );


  const handleLogout = async () => {
    await logout();
    setShowUserMenu(false);
    navigate('/login');
  };


  // Helper function to determine if bottom nav should be hidden
  const shouldHideBottomNav = () => {
    const path = location.pathname;
    
    // Hide on projects page (both listing and individual project pages)
    // if (path === '/projects' || path.startsWith('/projects/')) {
    //   return true;
    // }

    // Hide on chat pages: /projects/{projectId}/chats/{chatId}
    // Match pattern: /projects/ followed by UUID, then /chats/, then UUID
    const chatPagePattern = /^\/projects\/[^/]+\/chats\/[^/]+$/;
    if (chatPagePattern.test(path)) {
      return true;
    }
    
    // Hide on starters detail pages (but not main starters page)
    if (path.startsWith('/starters/') && path !== '/starters/') {
      return true;
    }
    
    return false;
  };

  const getDisplayName = (user) => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    } else if (user?.firstName) {
      return user.firstName;
    } else if (user?.username) {
      return user.username;
    }
    return 'User';
  };

  const getInitials = (user) => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    } else if (user?.firstName) {
      return user.firstName[0].toUpperCase();
    } else if (user?.username) {
      return user.username[0].toUpperCase();
    }
    return 'U';
  };

  // Determine smart home link destination for logged in users
  const getSmartHomeLink = () => {
    if (!isAuthenticated) {
      return '/'; // Default home for non-authenticated users
    }
    
    // Always go to home page for logged-in users
    return '/new';
  };

  return (
    <HeaderSlotContext.Provider value={headerSlotValue}>
    <div 
      className="h-screen flex flex-col bg-white dark:bg-gray-900 safe-top safe-left safe-right"
      style={{
        height: isKeyboardOpen ? `${viewportHeight - keyboardHeight}px` : '100vh',
        paddingBottom: isKeyboardOpen ? 0 : 'env(safe-area-inset-bottom)',
      }}
    >
      {/* Header - Hidden on mobile and for non-authenticated users on homepage */}
      {!(isAuthenticated === false && location.pathname === '/') && (
        <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 text-black dark:text-white sticky top-0 z-50 flex-shrink-0 hidden md:block">
        <div className="grid grid-cols-[auto,1fr,auto] items-center h-14 mx-auto px-6 w-full gap-6">
          {/* Left side: Logo + Nav */}
          <div className="flex items-center gap-8">
            <div className="flex items-center space-x-2 text-sm pr-6">
              <Link to={getSmartHomeLink()} className="flex items-center space-x-1">
                <img 
                  src="/logo_black.webp" 
                  alt="OKIO" 
                  className="h-6 w-auto dark:hidden"
                />
                <img 
                  src="/logo_white.webp" 
                  alt="OKIO" 
                  className="h-6 w-auto hidden dark:block"
                />
              </Link>
            </div>

            {isAuthenticated && (
              <nav className="flex items-center space-x-6">
                <button
                  onClick={() => {
                    navigate('/projects', {
                      state: {
                        previousPath: location.pathname,
                        transitionDirection: 'forward',
                      },
                    });
                  }}
                  className={`relative inline-flex items-center text-sm font-medium transition-colors ${
                    location.pathname.startsWith('/projects')
                      ? 'text-blue-600 dark:text-blue-400 font-semibold'
                      : 'text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400'
                  }`}
                >
                  <span>Projects</span>
                  {hasActiveProject && (
                    <span className="absolute -top-1 -right-2 w-2 h-2 bg-red-500 rounded-full" />
                  )}
                </button>
                <button
                  onClick={() => {
                    navigate('/starters', {
                      state: {
                        previousPath: location.pathname,
                        transitionDirection: 'forward',
                      },
                    });
                  }}
                  className={`text-sm font-medium transition-colors ${
                    location.pathname.startsWith('/starters')
                      ? 'text-blue-600 dark:text-blue-400 font-semibold'
                      : 'text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400'
                  }`}
                >
                  Starters
                </button>
                <Link
                  to="/discover"
                  className={`text-sm font-medium transition-colors ${
                    location.pathname === '/discover'
                      ? 'text-blue-600 dark:text-blue-400 font-semibold'
                      : 'text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400'
                  }`}
                >
                  Discover
                </Link>
                <Link
                  to="/profile"
                  className={`text-sm font-medium transition-colors ${
                    location.pathname.startsWith('/profile')
                      ? 'text-blue-600 dark:text-blue-400 font-semibold'
                      : 'text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400'
                  }`}
                >
                  Profile
                </Link>
                {user?.isSuperuser && (
                  <button
                    onClick={() => {
                      navigate('/admin');
                    }}
                    className={`text-sm font-medium transition-colors ${
                      location.pathname.startsWith('/admin')
                        ? 'text-blue-600 dark:text-blue-400 font-semibold'
                        : 'text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400'
                    }`}
                  >
                    Admin
                  </button>
                )}
              </nav>
            )}
          </div>

          {/* Center: Desktop Search Slot */}
          <div
            className={`hidden lg:flex justify-center transition-all duration-200 w-full ${
              isDesktopSearchVisible
                ? 'opacity-100 px-4'
                : 'opacity-0 pointer-events-none px-0'
            }`}
            aria-hidden={!isDesktopSearchVisible}
          >
            <div id={HEADER_SEARCH_SLOT_ID} className="w-full max-w-lg" />
          </div>

          {/* Right side: User Menu */}
          <div className="flex gap-3 items-center justify-end">

            {isAuthenticated ? (
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center bg-gray-200 dark:bg-gray-600 border dark:border-gray-500 text-black dark:text-white rounded-full text-xs font-medium"
                >
                  <div className="w-8 h-8 bg-blue-600 dark:bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold overflow-hidden">
                    {user?.avatar ? (
                      <img 
                        src={getImageUrl(user.avatar)} 
                        alt={getDisplayName(user)} 
                        className="w-8 h-8 rounded-full object-cover" 
                      />
                    ) : (
                      getInitials(user)
                    )}
                  </div>
                </button>
                
                {/* User Dropdown Menu */}
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-600 py-2 z-50">
                    <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-600">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{getDisplayName(user)}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{user?.email}</p>
                    </div>
                    <Link
                      to="/profile"
                      className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <UserIcon className="w-4 h-4 mr-3" />
                      Profile
                    </Link>
                    
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <ArrowRightEndOnRectangleIcon className="w-4 h-4 mr-3" />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 transform hover:scale-105"
              >
                Sign In
              </Link>
            )}
          </div>
        </div>
      </header>
      )}

      {/* Click outside to close menu */}
      {showUserMenu && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowUserMenu(false)}
        />
      )}

      {/* Main content */}
      <main 
        className={`flex-1 min-h-0 ${
          // Detail pages need their own scrolling (content flows naturally)
          (location.pathname.includes('/projects/') && !location.pathname.endsWith('/projects')) ||
          (location.pathname.startsWith('/starters/') && location.pathname !== '/starters') ||
          (location.pathname.startsWith('/discover/') && location.pathname !== '/discover' && location.pathname !== '/discover/') ||
          location.pathname.startsWith('/users/') ||
          location.pathname === '/profile' ||
          location.pathname === '/welcome' ||
          location.pathname === '/new'
            ? 'overflow-auto mobile-scroll'
            // Main listing pages manage their own internal scroll containers
            : 'overflow-hidden'
        } ${
          isAuthenticated && !shouldHideBottomNav() 
            ? 'sm:mb-0' 
            : 'mb-0'
        }`}
        style={{
          paddingBottom: isKeyboardOpen && isMobile ? '1rem' : undefined,
        }}
      >
        {children}
      </main>

      {/* Mobile Bottom Navigation - Only show when authenticated and on mobile, hide on certain pages */}
      {isAuthenticated && !shouldHideBottomNav() && (
        <nav 
          className={`md:hidden fixed left-0 right-0 bg-white dark:bg-gray-900 z-[50000] mobile-bottom-nav border-t dark:border-t-0 border-gray-200 dark:border-gray-700 transition-transform duration-300 ease-in-out ${
            isKeyboardOpen ? 'hidden' : isBottomNavVisible ? 'block translate-y-0' : 'block translate-y-full'
          }`}
          style={{
            bottom: isKeyboardOpen ? 'auto' : 0,
            paddingBottom: 'env(safe-area-inset-bottom)',
          }}
        >
          <div className="flex justify-between items-center">

             {/* Projects */}
             <button
              onClick={() => {
                navigate('/projects', {
                  state: {
                    previousPath: location.pathname,
                    transitionDirection: 'forward',
                  },
                });
              }}
              className={`flex-1 flex flex-col items-center p-2 rounded transition-colors mobile-button no-tap-highlight ripple-effect ${
                location.pathname.startsWith('/projects') 
                  ? 'text-black dark:text-white' 
                  : 'text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400'
              }`}
            >
              <div className="relative">
                {location.pathname.startsWith('/projects') ? (
                  <PaperAirplaneIconSolid className="w-6 h-6" />
                ) : (
                  <PaperAirplaneIcon className="w-6 h-6" />
                )}
                {hasActiveProject && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full" />
                )}
              </div>
              <span className="text-xs mt-1 font-medium">Projects</span>
            </button>

            {/* Starters */}
            <button
              onClick={() => {
                navigate('/starters', {
                  state: {
                    previousPath: location.pathname,
                    transitionDirection: 'forward',
                  },
                });
              }}
              className={`flex-1 flex flex-col items-center p-2 rounded transition-colors mobile-button no-tap-highlight ripple-effect ${
                location.pathname.startsWith('/starters') 
                  ? 'text-black dark:text-white' 
                  : 'text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400'
              }`}
            >
              {location.pathname.startsWith('/starters') ? (
                <MapIconSolid className="w-6 h-6" />
              ) : (
                <MapIcon className="w-6 h-6" />
              )}
              <span className="text-xs mt-1 font-medium">Starters</span>
            </button>


            {/* Discover */}
            <button
              onClick={() => {
                navigate('/discover', {
                  state: {
                    previousPath: location.pathname,
                    transitionDirection: 'forward',
                  },
                });
              }}
              className={`flex-1 flex flex-col items-center p-2 rounded transition-colors mobile-button no-tap-highlight ripple-effect ${
                location.pathname.startsWith('/discover') 
                  ? 'text-black dark:text-white' 
                  : 'text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400'
              }`}
            >
              {location.pathname.startsWith('/discover') ? (
                <UsersIconSolid className="w-6 h-6" />
              ) : (
                <UsersIcon className="w-6 h-6" />
              )}
              <span className="text-xs mt-1 font-medium">Discover</span>
            </button>


            {/* Profile */}
            <Link
              to="/profile"
              className={`flex-1 flex flex-col items-center p-2 rounded transition-colors mobile-button no-tap-highlight ripple-effect ${
                location.pathname === '/profile' 
                  ? 'text-black dark:text-white' 
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <div className="relative">
                {user?.avatar ? (
                  <img 
                    src={getImageUrl(user.avatar)} 
                    alt={getDisplayName(user)} 
                    className="w-6 h-6 rounded-full object-cover" 
                  />
                ) : (
                  location.pathname === '/profile' ? (
                    <UserIconSolid className="w-6 h-6" />
                  ) : (
                    <UserIcon className="w-6 h-6" />
                  )
                )}
              </div>
              <span className="text-xs mt-1">Profile</span>
            </Link>
          </div>
        </nav>
      )}
    </div>
    </HeaderSlotContext.Provider>
  );
};

export default Layout; 